/**
 * Decisión de App Check (ADR-SEC-023): ¿se inicializa, con qué clave, o se lanza?
 *
 * Es una función pura a propósito. `src/services/firebase/config.ts` corre al
 * importarse y termina creando los servicios, así que lo que decide ahí sólo se
 * puede probar stubbeando medio SDK (specs/DEUDA-INICIAL.md §13). La decisión vive
 * aquí, con su suite; el I/O (`initializeAppCheck`) se queda en `config.ts`.
 *
 * Reglas:
 * - Con los emuladores NO se inicializa: no exigen App Check, `localhost` no está en
 *   ninguna llave y la suite E2E arranca ese mismo servidor.
 * - Fuera de los emuladores, sin clave (ausente, vacía o sólo espacios) se LANZA:
 *   la app no monta y la consola nombra la variable. Nunca se inicializa App Check
 *   con una clave vacía en silencio.
 *
 * `quizoteca-student-portal` y `quizoteca-backoffice` copian este módulo tal cual
 * (mismo nombre de variable, misma condición, misma prueba).
 */

/** Nombre de la variable de entorno (specs/COHERENCIA.md §3). */
export const APP_CHECK_SITE_KEY_ENV = 'VITE_APPCHECK_RECAPTCHA_SITE_KEY'

export type AppCheckDecision =
  | { readonly kind: 'skip'; readonly reason: 'emulators' }
  | { readonly kind: 'init'; readonly siteKey: string }

export interface AppCheckDecisionInput {
  /**
   * Verdadero cuando el arranque habla con los emuladores. Debe ser LA MISMA
   * condición que gobierna `connect*Emulator` en `config.ts`.
   */
  readonly usesEmulators: boolean
  /** Valor crudo de `VITE_APPCHECK_RECAPTCHA_SITE_KEY` tal como lo entrega Vite. */
  readonly siteKey: string | undefined
}

export class MissingAppCheckSiteKeyError extends Error {
  constructor() {
    super(
      `[App Check] Falta ${APP_CHECK_SITE_KEY_ENV} en el .env.<modo> con el que se construyó ` +
        'este bundle. Fuera de los emuladores el admin no arranca sin atestación (ADR-SEC-023); ' +
        'el valor acordado de cada entorno está en specs/COHERENCIA.md §8.'
    )
    this.name = 'MissingAppCheckSiteKeyError'
  }
}

export function decideAppCheck(input: AppCheckDecisionInput): AppCheckDecision {
  if (input.usesEmulators) {
    return { kind: 'skip', reason: 'emulators' }
  }
  const siteKey = input.siteKey?.trim() ?? ''
  if (siteKey === '') {
    throw new MissingAppCheckSiteKeyError()
  }
  return { kind: 'init', siteKey }
}
